import {
  FIRM_LOGO_FILL_KINDS,
  type FirmLogoFillKind,
  type FirmLogoFillSummary,
} from "../../../api/firmLogoFill.service.js";
import {
  KIND_LABELS,
  OTHER_OUTCOMES_LABEL,
  otherReasonsOf,
  PICKABLE_REASONS,
  reasonTextOf,
  REASONS_BY_KIND,
} from "./outcomeLabels.js";
import styles from "./FirmLogoCopy.module.css";

type OutcomeSummaryProps = {
  summary: FirmLogoFillSummary;
  selectedReason: string | undefined;
  onSelectReason: (reason: string | undefined) => void;
};

type OutcomeChipProps = {
  label: string;
  count: number;
  done: number;
  total: number;
  isSelected: boolean;
  onSelect: () => void;
};

type ProgressCounts = {
  done: number;
  total: number;
};

type OutcomeGroupProps = {
  label: string;
  count: number;
  done: number;
  total: number;
  reasons: readonly string[];
  progressReasons: readonly string[];
  summary: FirmLogoFillSummary;
  selectedReason: string | undefined;
  onSelectReason: (reason: string) => void;
};

const PROGRESS_REASONS_BY_KIND: Record<FirmLogoFillKind, readonly string[]> = {
  write: [],
  unchanged: [],
  review: PICKABLE_REASONS,
  note: [],
};

function resolvedCountOf(
  resolvedCounts: Partial<Record<string, number>> | undefined,
  key: string,
): number {
  return resolvedCounts?.[key] ?? 0;
}

function progressOf(summary: FirmLogoFillSummary, reasons: readonly string[]): ProgressCounts {
  return reasons.reduce(
    (progress, reason) => ({
      done: progress.done + resolvedCountOf(summary.resolvedByReason, reason),
      total: progress.total + (summary.byReason[reason] ?? 0),
    }),
    { done: 0, total: 0 },
  );
}

function OutcomeProgress({ done, total }: ProgressCounts) {
  if (done === 0) {
    return null;
  }

  return (
    <span className={styles.outcomeProgress}>
      <span aria-hidden="true">· </span>
      {`${done} of ${total} done`}
    </span>
  );
}

function isSingleReasonKind(kind: FirmLogoFillKind): boolean {
  return REASONS_BY_KIND[kind].length === 1;
}

function OutcomeChip({ label, count, done, total, isSelected, onSelect }: OutcomeChipProps) {
  return (
    <button
      type="button"
      className={styles.outcomeChip}
      aria-pressed={isSelected}
      onClick={onSelect}
    >
      {label}
      <span className={styles.outcomeCount}>{count}</span>
      <OutcomeProgress done={done} total={total} />
    </button>
  );
}

function OutcomeGroup({
  label,
  count,
  done,
  total,
  reasons,
  progressReasons,
  summary,
  selectedReason,
  onSelectReason,
}: OutcomeGroupProps) {
  const reportedReasons = reasons.filter(reason => (summary.byReason[reason] ?? 0) > 0);

  return (
    <div className={styles.outcomeLine}>
      <h2 className={styles.kindHeading}>
        {label}
        <span className={styles.kindCount}>{count}</span>
        <OutcomeProgress done={done} total={total} />
      </h2>
      {reportedReasons.map(reason => {
        const reasonTotal = summary.byReason[reason] ?? 0;
        const reasonDone = progressReasons.includes(reason)
          ? resolvedCountOf(summary.resolvedByReason, reason)
          : 0;

        return (
          <OutcomeChip
            key={reason}
            label={reasonTextOf(reason).label}
            count={reasonTotal - reasonDone}
            done={reasonDone}
            total={reasonTotal}
            isSelected={selectedReason === reason}
            onSelect={() => onSelectReason(reason)}
          />
        );
      })}
    </div>
  );
}

export function OutcomeSummary({ summary, selectedReason, onSelectReason }: OutcomeSummaryProps) {
  const otherReasons = otherReasonsOf(summary.byReason, selectedReason);
  const otherCount = otherReasons.reduce(
    (total, reason) => total + (summary.byReason[reason] ?? 0),
    0,
  );
  const singleReasonKinds = FIRM_LOGO_FILL_KINDS.filter(isSingleReasonKind);
  const groupedKinds = FIRM_LOGO_FILL_KINDS.filter(kind => !isSingleReasonKind(kind));

  return (
    <section className={styles.summary} aria-label="Outcomes">
      <div className={styles.outcomeLine}>
        <OutcomeChip
          label="All outcomes"
          count={summary.totalRows}
          done={0}
          total={summary.totalRows}
          isSelected={selectedReason === undefined}
          onSelect={() => onSelectReason(undefined)}
        />
      </div>

      <div className={styles.outcomeLine}>
        {singleReasonKinds.map(kind => {
          const [reason] = REASONS_BY_KIND[kind];

          return (
            <OutcomeChip
              key={kind}
              label={KIND_LABELS[kind]}
              count={summary.byKind[kind] ?? 0}
              done={0}
              total={summary.byKind[kind] ?? 0}
              isSelected={selectedReason === reason}
              onSelect={() => onSelectReason(reason)}
            />
          );
        })}
      </div>

      {groupedKinds.map(kind => {
        const progressReasons = PROGRESS_REASONS_BY_KIND[kind];
        const progress = progressOf(summary, progressReasons);
        const count = (summary.byKind[kind] ?? 0) - progress.done;

        return (
          <OutcomeGroup
            key={kind}
            label={KIND_LABELS[kind]}
            count={count}
            done={progress.done}
            total={progress.total}
            reasons={REASONS_BY_KIND[kind]}
            progressReasons={progressReasons}
            summary={summary}
            selectedReason={selectedReason}
            onSelectReason={onSelectReason}
          />
        );
      })}

      {otherReasons.length > 0 ? (
        <OutcomeGroup
          label={OTHER_OUTCOMES_LABEL}
          count={otherCount}
          done={0}
          total={otherCount}
          reasons={otherReasons}
          progressReasons={[]}
          summary={summary}
          selectedReason={selectedReason}
          onSelectReason={onSelectReason}
        />
      ) : null}

      {selectedReason === undefined ? null : (
        <p className={styles.outcomeExplanation}>{reasonTextOf(selectedReason).explanation}</p>
      )}
    </section>
  );
}
